// Code Generated by Sidekick is for learning and experimentation purposes only.
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import React, { useEffect, useRef } from 'react';
import { ApiEndpoints } from '../../../constants/appConstants';
import httpService from '../../../services/httpService';
import { alphaNumericSort, ProviderType } from '../../ProviderProfile/models';
import { useAppContext } from '../../../Context';

type RHCalcMeasuresModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  reviewYear?: number;
};

const RHCalcMeasuresModal: React.FC<RHCalcMeasuresModalProps> = ({
  isOpen,
  onClose,
  reviewYear: propReviewYear,
}) => {
  const [selectedMeasures, setSelectedMeasures] = React.useState<any[]>([]);
  const [availableMeasures, setAvailableMeasures] = React.useState<any[]>([]);
  const [statusMessage, setStatusMessage] = React.useState<string>('');
  const [isError, setIsError] = React.useState<boolean>(false);
  const multiSelectRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { reviewYear: contextReviewYear } = useAppContext();
  const reviewYear = propReviewYear ?? contextReviewYear;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      try {
        const target = event.target as Node;
        const clickedInsideContainer = containerRef.current?.contains(target);
        const panel = document.querySelector('.p-multiselect-panel');
        const clickedInsidePanel = panel?.contains(target);

        if (!clickedInsideContainer && !clickedInsidePanel) {
          multiSelectRef.current?.hide();
        }
      } catch (error: any) {
        console.error(
          '[RHCalcMeasuresModal] Error handling click outside MultiSelect.',
          {
            message: error?.message,
            stack: error?.stack,
          }
        );
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !reviewYear) {
      return;
    }

    const fetchCalculatedMeasures = async () => {
      try {
        console.log('[RHCalcMeasuresModal] Fetching calculated measures.', {
          providerType: ProviderType.RS,
          reviewYear,
        });

        const response: any = await httpService.get(
          `${ApiEndpoints.getCalculatedMeasures}/${ProviderType.RS}/${reviewYear}`
        );

        const measures = Array.isArray(response?.data) ? response.data : [];

        const sorted = [...measures].sort((a: any, b: any) =>
          alphaNumericSort(a?.measureNumber, b?.measureNumber)
        );

        setAvailableMeasures(sorted);

        console.log(
          '[RHCalcMeasuresModal] Calculated measures fetched successfully.',
          {
            providerType: ProviderType.RS,
            reviewYear,
            measureCount: sorted.length,
          }
        );
      } catch (error: any) {
        setAvailableMeasures([]);
        setIsError(true);
        setStatusMessage(
          'Error loading calculated measures. Please try again later.'
        );

        console.error(
          '[RHCalcMeasuresModal] Error fetching calculated measures.',
          {
            providerType: ProviderType.RS,
            reviewYear,
            message: error?.message,
            stack: error?.stack,
            responseData: error?.response?.data,
            responseStatus: error?.response?.status,
          }
        );
      }
    };

    void fetchCalculatedMeasures();
  }, [isOpen, reviewYear]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedMeasures([]);
      setStatusMessage('');
      setIsError(false);
    }
  }, [isOpen]);

  const handleCalculate = async () => {
    try {
      if (selectedMeasures.length === 0) {
        setIsError(true);
        setStatusMessage(
          'Please select at least one measure to perform calculations.'
        );
        return;
      }

      const payload = {
        standardMeasureIds: selectedMeasures,
        providerType: ProviderType.RS,
      };

      setIsError(false);
      setStatusMessage('Calculations in progress. Please wait...');

      console.log('[RHCalcMeasuresModal] Starting calculations.', {
        providerType: ProviderType.RS,
        selectedMeasureCount: selectedMeasures.length,
      });

      await httpService.post(ApiEndpoints.performCalculations, payload);

      setIsError(false);
      setStatusMessage('Calculations performed successfully.');

      console.log('[RHCalcMeasuresModal] Calculations completed successfully.', {
        providerType: ProviderType.RS,
        selectedMeasureCount: selectedMeasures.length,
      });
    } catch (error: any) {
      setIsError(true);
      setStatusMessage(
        'Error performing calculations. Please try again later.'
      );

      console.error('[RHCalcMeasuresModal] Error performing calculations.', {
        providerType: ProviderType.RS,
        selectedMeasureCount: selectedMeasures.length,
        message: error?.message,
        stack: error?.stack,
        responseData: error?.response?.data,
        responseStatus: error?.response?.status,
      });
    }
  };

  const pushCalculatedData = async () => {
    try {
      setIsError(false);
      setStatusMessage('Pushing data to Intake. Please wait...');

      console.log('[RHCalcMeasuresModal] Pushing calculated data to Intake.', {
        providerType: ProviderType.RS,
      });

      await httpService.get(
        `${ApiEndpoints.pushCalculatedData}/${ProviderType.RS}`
      );

      setIsError(false);
      setStatusMessage('Calculated data pushed to Intake successfully.');

      console.log(
        '[RHCalcMeasuresModal] Calculated data pushed successfully.',
        {
          providerType: ProviderType.RS,
        }
      );
    } catch (error: any) {
      setIsError(true);
      setStatusMessage(
        'Error pushing calculated data to Intake. Please try again later.'
      );

      console.error(
        '[RHCalcMeasuresModal] Error pushing calculated data to Intake.',
        {
          providerType: ProviderType.RS,
          message: error?.message,
          stack: error?.stack,
          responseData: error?.response?.data,
          responseStatus: error?.response?.status,
        }
      );
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      header="Perform Calculations"
      visible={isOpen}
      style={{ width: '50vw', minHeight: '20vw' }}
      onHide={onClose}
      draggable={false}
      resizable={false}
    >
      <div className="p-fluid p-4">
        <div className="flex mb-4">
          <Button
            label="Push To Intake"
            icon="pi pi-upload"
            onClick={() => {
              void pushCalculatedData();
            }}
            className="p-button-secondary"
            style={{ width: 'auto', marginLeft: 'auto' }}
            loading={
              statusMessage === 'Pushing data to Intake. Please wait...'
            }
          />
        </div>

        <div className="field" ref={containerRef}>
          <label htmlFor="measures" className="block mb-2 font-medium">
            Select Measures
          </label>

          <MultiSelect
            ref={multiSelectRef}
            id="measures"
            placeholder="Select Measures"
            className="w-full"
            display="chip"
            value={selectedMeasures}
            options={availableMeasures}
            optionLabel="measureNumber"
            optionValue="standardMeasureId"
            onChange={(e) => setSelectedMeasures(e.value)}
            maxSelectedLabels={5}
            appendTo={document.body}
          />
        </div>

        <div
          className="flex justify-center mt-4"
          style={styles.justifyContentCenter}
        >
          <Button
            label="Calculate"
            icon="pi pi-calculator"
            className="p-button-success"
            onClick={() => {
              void handleCalculate();
            }}
            loading={
              statusMessage === 'Calculations in progress. Please wait...'
            }
            style={{ width: 'auto' }}
          />
        </div>
      </div>

      {statusMessage && (
        <div className="p-4 pt-0">
          <div className="p-3 bg-blue-50 border-round">
            <p
              className={`m-0 ${
                isError ? 'text-red-600' : 'text-green-600'
              }`}
            >
              {statusMessage}
            </p>
          </div>
        </div>
      )}
    </Dialog>
  );
};

const styles = {
  justifyContentCenter: {
    display: 'flex',
    justifyContent: 'center',
  },
};

export default RHCalcMeasuresModal;